/**
 * AI Chat Service
 * Handles communication with the ai-chat Supabase Edge Function
 * Two-Tier Context System:
 *   Tier 1 — Always-on business profile (identity, pricing, team, aggregates, today's schedule)
 *   Tier 2 — View-aware detail injected based on currentView
 */
import { supabase } from './supabase';
import { getWorkspaceIdForAi } from './database';
import {
  isRetryableAiError, friendlyAiBusyMessage, RETRY_DELAYS_MS,
  redactSecrets, isAiAuthError, friendlyAiAuthMessage, errorToText,
  detailFromAiErrorBody, isAiAllowanceError, friendlyAiAllowanceMessage,
} from './aiRetryHelpers';
import type {
  BusinessSettings, BusinessMetrics,
  Client, Estimate, Project, Lead, Appointment,
  PricebookItem, ServiceTemplate, Equipment, Crew, TeamMember, TimeLog,
  ServiceContract,
} from '../types';

export interface ChatAttachment {
  url: string;
  mimeType: string;
  name: string;
  sizeBytes: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  attachments?: ChatAttachment[];
  /**
   * True when this "assistant" bubble is a locally-generated failure notice
   * (⚠️ …), not something the model said. Marked so it can be excluded from
   * the history replayed upstream — otherwise the model is told it previously
   * answered "The AI assistant is busy right now", and starts reasoning from
   * its own error messages.
   */
  isError?: boolean;
}

/** Accepted mime types for AI chat attachments. Gemini 3.1 Flash Lite handles all of these natively. */
export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const;

/** Per-file size ceiling. Gemini's inline limit is 20MB; we also don't want to stuff 100MB in a row. */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** Max attachments per outbound chat message. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/* ── Enriched CRM Context ─────────────────────────────────── */

interface CRMContext {
  // TIER 1 — compact business profile (always sent)
  business?: {
    name: string;
    industry: string;
    currency: string;
    serviceArea: string;
    description: string;
  };
  user?: { name: string; role: string };
  currentView?: string;

  pricingEngine?: {
    targetHourlyRate: number;
    breakEvenRate: number;
    targetMargin: number;
    materialMarkup: number;
    minServiceCall: number;
    tripCharge: number;
    emergencySurcharge: number;
  };

  teamOverview?: string;    // compact crew + employee summary
  activeClockIns?: string;  // who is currently working

  aggregates?: {
    totalClients: number;
    totalLeads: number;
    totalEstimates: number;
    activeProjects: number;
    totalEquipment: number;
    activeContracts: number;
    todayAppointments: number;
    pendingFollowUps: number;
    totalRevenuePipeline: number;
    openEstimateValue: number;
  };

  todaySchedule?: string;

  // TIER 2 — view-aware detail (only the relevant domain is expanded)
  clientsSummary?: string;
  clientsCount?: number;
  estimatesSummary?: string;
  estimatesCount?: number;
  projectsSummary?: string;
  projectsCount?: number;
  leadsSummary?: string;
  leadsCount?: number;
  appointmentsSummary?: string;
  pricebookSummary?: string;
  equipmentSummary?: string;
  contractsSummary?: string;
  industryType?: string; // kept for backward compat with edge function

  /** Screen-specific focus injected by "Ask Raza" contextual buttons. */
  contextualFocus?: string;
}

/* ── Helpers ───────────────────────────────────────────────── */

/** Whole-dollar money. Correct for totals, revenue and pipeline figures. */
const fmt$ = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/**
 * Per-unit money — always two decimals.
 *
 * fmt$ rounds to whole dollars, which is fine for a $12,400 estimate total and
 * catastrophic for a unit price: sod at $0.65/SF was being reported to the
 * model as "$1/SF" (a 54% overstatement of the cost basis), and anything under
 * $0.50 was reported as "$0" — i.e. the material is free. Use this for any
 * rate, unit cost or per-item figure.
 */
const fmtUnit$ = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Parses a bare YYYY-MM-DD as a LOCAL calendar date.
 *
 * `new Date('2026-08-07')` is parsed as UTC midnight and then rendered in the
 * viewer's zone, so every date-only string printed one day early for anyone
 * west of Greenwich. Matches parseLocalDate in utils/revenueTrends.ts.
 */
const parseLocalYMD = (d: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
};

const fmtDate = (d: string | undefined) => d ? parseLocalYMD(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A';

/**
 * The contractor's LOCAL calendar date as YYYY-MM-DD.
 *
 * This used to be `new Date().toISOString().split('T')[0]`, which is the UTC
 * date. Appointment.date is written as a local date (see formatLocalDate in
 * components/ScheduleModal.tsx), so from roughly 4-7pm local onward in any US
 * timezone the two disagreed and the assistant answered "what's left today?"
 * with TOMORROW's jobs — during exactly the hours a contractor asks.
 */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const cap = <T,>(arr: T[] | undefined, n: number) => (arr || []).slice(0, n);

/**
 * Appended to any list that was truncated, so the model can tell the
 * difference between "there are none" and "you weren't shown them all".
 * Without it a workspace with 300 clients and one with 50 produce
 * byte-identical-looking summaries, and the model asserts absence of records
 * that exist.
 */
const moreMarker = (total: number, shown: number) =>
  total > shown ? `\n… and ${total - shown} more not shown (ask about a specific one by name)` : '';

/**
 * Flattens contractor- and customer-authored free text before it is spliced
 * into the prompt.
 *
 * Notes, task descriptions and property notes are written by people whose role
 * may have NO AI access at all (a Foreman can add a project task), yet they
 * land verbatim in an Admin's prompt. Collapsing newlines stops a note from
 * forging its own "--- END BUSINESS DATA ---" style section break, and the
 * length cap bounds how much of the payload any single record can occupy.
 */
const asData = (s: string | undefined | null, max = 120): string =>
  (s ?? '').replace(/[\r\n]+/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, max);

/* ── Build CRM Context ─────────────────────────────────────── */

export function buildCRMContext(options: {
  userName?: string;
  userRole?: string;
  currentView?: string;
  // Business
  settings?: BusinessSettings;
  metrics?: BusinessMetrics;
  // CRM
  clients?: Client[];
  estimates?: Estimate[];
  projects?: Project[];
  leads?: Lead[];
  appointments?: Appointment[];
  // Operations
  pricebook?: PricebookItem[];
  serviceTemplates?: ServiceTemplate[];
  equipment?: Equipment[];
  crews?: Crew[];
  teamMembers?: TeamMember[];
  activeTimeLogs?: TimeLog[];
  // Services
  serviceContracts?: ServiceContract[];
  contextualFocus?: string;
}): CRMContext {
  const ctx: CRMContext = {};
  const view = options.currentView || '';

  if (options.contextualFocus?.trim()) {
    ctx.contextualFocus = options.contextualFocus.trim();
  }

  // ═══════════════════════════════════════════════
  //  TIER 1 — Always Sent
  // ═══════════════════════════════════════════════

  // User
  if (options.userName) {
    ctx.user = { name: options.userName, role: options.userRole || 'User' };
  }
  ctx.currentView = view;
  ctx.industryType = options.settings?.profile?.industryType || 'landscaping';

  // Business Profile
  const profile = options.settings?.profile;
  if (profile) {
    ctx.business = {
      name: profile.businessName || 'My Business',
      industry: profile.industryType || 'landscaping',
      currency: profile.currency || 'USD',
      serviceArea: profile.serviceArea || '',
      description: profile.businessDescription || '',
    };
  }

  // Pricing Engine
  const metrics = options.metrics;
  const pricing = options.settings?.pricing;
  const rules = options.settings?.rules;
  if (metrics && pricing && rules) {
    ctx.pricingEngine = {
      targetHourlyRate: Math.round((metrics.targetHourlyRate || 0) * 100) / 100,
      breakEvenRate: Math.round((metrics.breakEvenRate || 0) * 100) / 100,
      targetMargin: pricing.targetMarginPercent,
      materialMarkup: pricing.defaultMaterialMarkupPercent,
      minServiceCall: rules.minServiceCall,
      tripCharge: rules.tripCharge,
      emergencySurcharge: rules.emergencySurchargePercent,
    };
  }

  // Team Overview
  const crews = options.crews || [];
  const teamMembers = options.teamMembers || [];
  if (crews.length > 0 || teamMembers.length > 0) {
    const parts: string[] = [];
    if (teamMembers.length > 0) {
      parts.push(`${teamMembers.length} team members`);
    }
    if (crews.length > 0) {
      const crewSummaries = crews.map(c => {
        // +1 for the leader only when there IS one. A crew saved with the
        // Leader dropdown left blank was being reported with an extra head,
        // and an empty crew read as "(1 people)".
        const headcount = (c.memberIds || []).length + (c.leaderId ? 1 : 0);
        return `${c.name} (${headcount} people)`;
      });
      parts.push(`Crews: ${crewSummaries.join(', ')}`);
    }
    ctx.teamOverview = parts.join(' | ');
  }

  // Active Clock-Ins
  const activeLogs = options.activeTimeLogs || [];
  if (activeLogs.length > 0) {
    const clockedIn = activeLogs
      .filter(l => !l.clockOut)
      .map(l => {
        const since = new Date(l.clockIn).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        const proj = l.projectName ? ` on ${l.projectName}` : '';
        const status = l.status === 'Lunch' ? ' (lunch)' : '';
        return `${l.userName} since ${since}${proj}${status}`;
      });
    if (clockedIn.length > 0) {
      ctx.activeClockIns = clockedIn.join(', ');
    }
  }

  // Aggregates
  const clients = options.clients || [];
  const estimates = options.estimates || [];
  const projects = options.projects || [];
  const leads = options.leads || [];
  const appointments = options.appointments || [];
  const equipment = options.equipment || [];
  const contracts = options.serviceContracts || [];
  const todayStr = today();

  const activeProjects = projects.filter(p => p.status !== 'Completed' && p.status !== 'Cancelled');
  const todayAppts = appointments.filter(a => a.date === todayStr);
  // Due by end of the LOCAL day. `new Date('2026-08-08')` is UTC midnight,
  // which on the US west coast is 5pm the PREVIOUS day — so every lead due
  // tomorrow was being counted as already overdue from late afternoon on.
  const endOfLocalDay = new Date();
  endOfLocalDay.setHours(23, 59, 59, 999);
  const pendingFollowUps = leads.filter(
    l => l.nextActionDate && parseLocalYMD(l.nextActionDate) <= endOfLocalDay
  ).length;
  const openEstimates = estimates.filter(e => e.status === 'Draft' || e.status === 'Sent' || e.status === 'Review Needed');

  ctx.aggregates = {
    totalClients: clients.length,
    totalLeads: leads.length,
    totalEstimates: estimates.length,
    activeProjects: activeProjects.length,
    totalEquipment: equipment.length,
    activeContracts: contracts.filter(c => c.status === 'Active').length,
    todayAppointments: todayAppts.length,
    pendingFollowUps,
    totalRevenuePipeline: activeProjects.reduce((s, p) => s + (p.revenue || 0), 0),
    openEstimateValue: openEstimates.reduce((s, e) => s + (e.total || 0), 0),
  };

  // Today's Schedule (always)
  if (todayAppts.length > 0) {
    ctx.todaySchedule = todayAppts
      .sort((a, b) => a.startTime.localeCompare(b.startTime))
      .map(a => `${a.startTime} - ${a.title}: ${a.clientName}${a.clientAddress ? ` (${a.clientAddress})` : ''}`)
      .join(' | ');
  }

  // ═══════════════════════════════════════════════
  //  TIER 2 — View-Aware Detail
  // ═══════════════════════════════════════════════

  // Which domains get expanded detail, keyed on the REAL ViewState.
  //
  // This was a case-insensitive substring match against hand-written names,
  // and three of its eight branches were unreachable: no ViewState the
  // navigation layer can produce contains "lead", "crm", "fleet" or
  // "equipment" (leads live inside 'Clients'; the fleet screen is
  // 'Inventory'). Leads were therefore ALWAYS the compact form — no next
  // action, no phone, no notes — and equipment detail was never sent at all,
  // while the model was still told how many machines existed.
  //
  // An explicit map means the next view rename shows up as a missing key
  // instead of silently unwiring a branch. 'AIAssistant' is listed
  // deliberately: it is the dedicated Raza screen and it previously matched
  // nothing, so the primary AI surface received the least data of any page.
  const EXPAND: Record<string, ReadonlyArray<string>> = {
    Dashboard:      ['clients', 'leads'],
    Clients:        ['clients', 'leads'],
    FollowUps:      ['leads'],
    Estimates:      ['estimates', 'pricebook'],
    ProposalViewer: ['estimates'],
    Projects:       ['projects'],
    Calendar:       ['appointments'],
    Pricebook:      ['pricebook'],
    Inventory:      ['equipment'],
    Services:       ['contracts'],
    Maintenance:    ['contracts'],
    AIAssistant:    ['clients', 'leads'],
  };
  const expanded = new Set(EXPAND[view] ?? []);
  const isView = (domain: string) => expanded.has(domain);

  // Lifetime revenue per client, derived from COMPLETED projects — the same
  // rule the Clients screen uses. The persisted clients.total_spent column is
  // never recomputed and always reads 0, so reporting it told the assistant
  // every single client had spent nothing.
  const spentByClient = new Map<string, number>();
  projects.forEach(p => {
    if (p.status === 'Completed') {
      spentByClient.set(p.clientId, (spentByClient.get(p.clientId) || 0) + (p.revenue || 0));
    }
  });
  const spentOf = (clientId: string) => fmt$(spentByClient.get(clientId) || 0);

  // Clients — expanded when on CRM/Clients views, compact otherwise
  //
  // NOTE ON GATE CODES: the physical door/gate code for a customer's property
  // is deliberately NOT sent. It is the one field here that grants real-world
  // access to someone's home, it is of no use to the assistant when answering
  // business questions, and shipping it meant every code in the workspace went
  // to a third-party model — and was persisted into the saved transcript —
  // every time anyone opened the chat on the landing view. The model is told
  // one exists so it can tell the user where to look.
  if (isView('clients')) {
    ctx.clientsCount = clients.length;
    ctx.clientsSummary = cap(clients, 50)
      .map(c => `- ${c.name} | Ph: ${c.phone || '—'} | Email: ${c.email || '—'} | Addr: ${c.address || '—'} | Tags: ${(c.tags || []).join(', ') || 'none'} | Spent: ${spentOf(c.id)}${c.propertyNotes ? ` | Notes: ${asData(c.propertyNotes, 60)}` : ''}${c.gateCode ? ' | Gate code: on file' : ''}`)
      .join('\n') + moreMarker(clients.length, Math.min(clients.length, 50));
  } else {
    ctx.clientsCount = clients.length;
    ctx.clientsSummary = cap(clients, 20)
      .map(c => `- ${c.name} | ${c.phone || '—'} | Tags: ${(c.tags || []).join(', ') || 'none'} | Spent: ${spentOf(c.id)}`)
      .join('\n') + moreMarker(clients.length, Math.min(clients.length, 20));
  }

  // Estimates — expanded when on Estimates view
  if (isView('estimates')) {
    ctx.estimatesCount = estimates.length;
    ctx.estimatesSummary = cap(estimates, 50)
      .map(e => {
        const items = e.items || [];
        // Unit rate and cost at two decimals, line total whole-dollar. Cost
        // and margin are included because the shipped "Help me hit my target
        // margin" prompt is unanswerable without a per-line cost basis — the
        // model was being handed quantity × rate and asked which line is thin.
        const lineItems = items.slice(0, 8).map(i => {
          const cost = typeof i.cost === 'number' ? `, cost ${fmtUnit$(i.cost)}` : '';
          const margin = typeof i.marginPercent === 'number' ? `, margin ${i.marginPercent.toFixed(0)}%` : '';
          return `   • ${i.type}: ${asData(i.description, 80)} — ${i.quantity} × ${fmtUnit$(i.rate)} = ${fmt$(i.total)}${cost}${margin}`;
        }).join('\n');
        const itemTail = items.length > 8 ? `\n   … and ${items.length - 8} more line items` : '';
        return `- ${e.clientName} | ${fmt$(e.total)} | Status: ${e.status} | Date: ${fmtDate(e.dateCreated)}${e.validUntil ? ` | Valid until: ${fmtDate(e.validUntil)}` : ''}\n${lineItems}${itemTail}`;
      })
      .join('\n') + moreMarker(estimates.length, Math.min(estimates.length, 50));
  } else {
    ctx.estimatesCount = estimates.length;
    ctx.estimatesSummary = cap(estimates, 20)
      .map(e => `- ${e.clientName} | ${fmt$(e.total)} | Status: ${e.status} | Date: ${fmtDate(e.dateCreated)}`)
      .join('\n');
  }

  // Projects — expanded when on Projects view.
  // projectsCount counts ALL projects while the summary lists only ACTIVE
  // ones, so the completed tail is named explicitly rather than leaving the
  // model to reconcile "143 projects" against 20 rows.
  const completedProjects = projects.filter(p => p.status === 'Completed');
  const completedTail = completedProjects.length
    ? `\n(${completedProjects.length} completed projects are not listed; their revenue is included in each client's lifetime spend)`
    : '';
  if (isView('projects')) {
    ctx.projectsCount = projects.length;
    ctx.projectsSummary = cap(activeProjects, 50)
      .map(p => {
        const tasksDone = (p.tasks || []).filter(t => t.isCompleted).length;
        const tasksTotal = (p.tasks || []).length;
        const taskList = (p.tasks || []).slice(0, 5).map(
          t => `   • [${t.isCompleted ? 'x' : ' '}] ${asData(t.description, 80)}${t.priority ? ` (${t.priority})` : ''}`
        ).join('\n');
        const matPending = (p.materialStatus || []).filter(m => m.status === 'Pending').length;
        return `- ${p.name} (${p.clientName}) | ${p.status} | ${p.completionPercent}% done | Rev: ${fmt$(p.revenue || 0)} | Tasks: ${tasksDone}/${tasksTotal}${matPending ? ` | ${matPending} materials pending` : ''}${p.estimatedLaborHours ? ` | Est hrs: ${p.estimatedLaborHours}` : ''}\n${taskList}`;
      })
      .join('\n') + moreMarker(activeProjects.length, Math.min(activeProjects.length, 50)) + completedTail;
  } else {
    ctx.projectsCount = projects.length;
    ctx.projectsSummary = cap(activeProjects, 20)
      .map(p => `- ${p.name} (${p.clientName}) | ${p.status} | ${p.completionPercent}% | Rev: ${fmt$(p.revenue || 0)}`)
      .join('\n') + moreMarker(activeProjects.length, Math.min(activeProjects.length, 20)) + completedTail;
  }

  // Leads — expanded on the screens that actually show them (Clients hosts the
  // Leads sub-tab; FollowUps is the chase list). The old guard tested for a
  // 'Lead'/'CRM' view that the router can never produce, so the next action
  // and its due date — the whole point of "who do I need to follow up with?" —
  // never reached the model.
  if (isView('leads')) {
    ctx.leadsCount = leads.length;
    ctx.leadsSummary = cap(leads, 50)
      .map(l => `- ${l.name} | ${l.status} | Interest: ${l.serviceInterest || '—'} | Val: ${fmt$(l.value || 0)} | Ph: ${l.phone || '—'} | Addr: ${l.address || '—'}${l.nextAction ? ` | Next: ${asData(l.nextAction, 80)}${l.nextActionDate ? ` by ${fmtDate(l.nextActionDate)}` : ''}` : ''}${l.notes ? ` | Notes: ${asData(l.notes, 60)}` : ''}`)
      .join('\n') + moreMarker(leads.length, Math.min(leads.length, 50));
  } else {
    ctx.leadsCount = leads.length;
    ctx.leadsSummary = cap(leads, 20)
      .map(l => `- ${l.name} | ${l.status} | Interest: ${l.serviceInterest || '—'} | Val: ${fmt$(l.value || 0)}`)
      .join('\n') + moreMarker(leads.length, Math.min(leads.length, 20));
  }

  // Appointments — expanded when on the Calendar
  if (isView('appointments')) {
    ctx.appointmentsSummary = cap(appointments, 50)
      .map(a => `- ${a.date} ${a.startTime}-${a.endTime}: ${a.title} — ${a.clientName}${a.clientAddress ? ` at ${a.clientAddress}` : ''} (${a.type})${a.notes ? ` | Notes: ${asData(a.notes, 60)}` : ''}`)
      .join('\n') + moreMarker(appointments.length, Math.min(appointments.length, 50));
  } else if (todayAppts.length > 0) {
    // Always include today's appointments at minimum
    ctx.appointmentsSummary = todayAppts
      .map(a => `- ${a.startTime}-${a.endTime}: ${a.title} — ${a.clientName} (${a.type})`)
      .join('\n');
  }

  // Pricebook — expanded when on Pricebook/Estimates view
  const pricebook = options.pricebook || [];
  if (isView('pricebook') && pricebook.length > 0) {
    // Unit cost at two decimals — see fmtUnit$. Sod at $0.65/SF was being
    // quoted to the model as "$1/SF".
    ctx.pricebookSummary = cap(pricebook, 50)
      // `markupPercent` nulo significa «sigue el dial del taller», no 0%. Sin
      // este ternario el modelo leía "Markup: null%" y podía razonar sobre un
      // markup inexistente.
      .map(p => `- ${p.name} | Cat: ${p.category} | Cost: ${fmtUnit$(p.cost)}/${p.unit} | Markup: ${p.markupPercent === null || p.markupPercent === undefined ? 'workspace default' : `${p.markupPercent}%`}${p.vendor ? ` | Vendor: ${p.vendor}` : ''}`)
      .join('\n') + moreMarker(pricebook.length, Math.min(pricebook.length, 50));
  }

  // Equipment — expanded on the fleet screen, whose ViewState is 'Inventory'
  if (isView('equipment') && equipment.length > 0) {
    ctx.equipmentSummary = cap(equipment, 30)
      .map(e => `- ${e.name} (${e.type}) | Status: ${e.status} | Location: ${e.location}${e.nextServiceDate ? ` | Next service: ${fmtDate(e.nextServiceDate)}` : ''}${e.notes ? ` | ${asData(e.notes, 50)}` : ''}`)
      .join('\n') + moreMarker(equipment.length, Math.min(equipment.length, 30));
  }

  // Service Contracts — expanded when on Contracts/Services view.
  // Status is per row because the list is unfiltered while
  // aggregates.activeContracts counts only Active ones.
  if (isView('contracts') && contracts.length > 0) {
    ctx.contractsSummary = cap(contracts, 30)
      .map(c => `- ${c.clientName} | ${c.frequency} | ${c.status} | Rate: ${c.billingType === 'Monthly' ? `${fmtUnit$(c.monthlyRate)}/mo` : `${fmtUnit$(c.visitRate)}/visit`}${c.preferredDayOfWeek ? ` | ${c.preferredDayOfWeek}` : ''} | ${c.serviceAddress}`)
      .join('\n') + moreMarker(contracts.length, Math.min(contracts.length, 30));
  }

  return ctx;
}

/* ── Send Chat Message ─────────────────────────────────────── */

export async function sendChatMessage(
  message: string,
  history: { role: string; text: string }[],
  context: CRMContext,
  attachments?: ChatAttachment[]
): Promise<{ reply: string; error?: string }> {
  // Up to 3 attempts (initial + 2 retries) for transient upstream failures.
  // The most common cause is Gemini returning 503 "UNAVAILABLE" during peak
  // load — Google's own message says spikes are "usually temporary."
  let lastDetail = '';
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { message, history, context, attachments, workspaceId: getWorkspaceIdForAi() },
      });

      if (error) {
        let detail = errorToText(error.message);
        try {
          if (error.context) {
            const body = await error.context.json();
            detail = detailFromAiErrorBody(body, error.message);
          }
        } catch (_) { /* ignore parse errors */ }

        // The monthly limit: no retry, and info not error — see aiRetryHelpers.
        if (isAiAllowanceError(detail)) {
          console.info('[AI Chat] monthly AI limit reached; not retrying');
          return { reply: '', error: friendlyAiAllowanceMessage() };
        }

        if (isRetryableAiError(detail) && attempt < RETRY_DELAYS_MS.length) {
          // Redacted: console.warn is patched into the error pipeline just like
          // console.error, so an unredacted upstream body here reaches
          // system_error_logs — the exact leak redactSecrets exists to stop.
          console.warn(`[AI Chat] retryable error on attempt ${attempt + 1}, retrying in ${RETRY_DELAYS_MS[attempt]}ms:`, redactSecrets(detail));
          lastDetail = detail;
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
          continue;
        }

        console.error('[AI Chat] Edge Function error:', redactSecrets(detail));
        if (isAiAuthError(detail)) return { reply: '', error: friendlyAiAuthMessage(history) };
        return { reply: '', error: isRetryableAiError(detail) ? friendlyAiBusyMessage(history) : `Failed to reach AI: ${redactSecrets(detail)}` };
      }

      if (data?.error) {
        const apiDetail = errorToText(data.error);
        if (isRetryableAiError(apiDetail) && attempt < RETRY_DELAYS_MS.length) {
          console.warn(`[AI Chat] retryable API error on attempt ${attempt + 1}, retrying:`, redactSecrets(apiDetail));
          lastDetail = apiDetail;
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
          continue;
        }
        console.error('[AI Chat] API error:', redactSecrets(apiDetail));
        if (isAiAuthError(apiDetail)) return { reply: '', error: friendlyAiAuthMessage(history) };
        return { reply: '', error: isRetryableAiError(apiDetail) ? friendlyAiBusyMessage(history) : redactSecrets(apiDetail) };
      }

      // The edge function returns { reply, modelUsed }. Anything else — a
      // non-string reply, a shape change, an empty body — must not reach the
      // renderer, which would try to run String.prototype methods on it.
      return { reply: typeof data?.reply === 'string' && data.reply.trim() ? data.reply : 'No response received.' };
    } catch (err: unknown) {
      const msg = errorToText(err instanceof Error ? err.message : err) || 'Something went wrong';
      if (isRetryableAiError(msg) && attempt < RETRY_DELAYS_MS.length) {
        console.warn(`[AI Chat] network exception on attempt ${attempt + 1}, retrying:`, redactSecrets(msg));
        lastDetail = msg;
        await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
        continue;
      }
      console.error('[AI Chat] Unexpected error:', redactSecrets(msg));
      return { reply: '', error: redactSecrets(msg) };
    }
  }

  // Exhausted all retries — surface the friendly busy message.
  console.error("[AI Chat] all retries exhausted, last error:", redactSecrets(lastDetail));
  return { reply: '', error: friendlyAiBusyMessage(history) };
}
